import type { FormEventHandler, PropsWithChildren, ReactNode } from "react";
import { t } from "ttag";

interface FunnelStepProps extends PropsWithChildren {
  title: ReactNode;
  description?: ReactNode;
  /** Optional note rendered between the fields and the advance button. */
  footerNote?: ReactNode;
  nextLabel?: string;
  nextDisabled?: boolean;
  isSubmitting?: boolean;
  onSubmit?: FormEventHandler<HTMLFormElement>;
}

const ArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="size-5"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

/**
 * Shared layout for a single levelsRx-style funnel step: large title, optional
 * description, the step's fields, an optional note and a full-width advance
 * button. Wraps everything in a <form> so fields submit via Enter/click.
 */
export const FunnelStep = ({
  title,
  description,
  footerNote,
  nextLabel = t`Next`,
  nextDisabled = false,
  isSubmitting = false,
  onSubmit,
  children,
}: FunnelStepProps) => (
  <form
    className="mx-auto w-full max-w-2xl px-5 py-8 sm:py-12"
    onSubmit={(event) => {
      event.preventDefault();
      onSubmit?.(event);
    }}
  >
    <h1 className="text-4xl leading-tight font-normal text-primary-950 sm:text-5xl">
      {title}
    </h1>

    {description && (
      <p className="mt-6 text-lg text-primary-950">{description}</p>
    )}

    <div className="mt-8">{children}</div>

    {footerNote && (
      <p className="mt-6 text-base text-primary-950">{footerNote}</p>
    )}

    <button
      type="submit"
      disabled={nextDisabled || isSubmitting}
      className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-primary-950 px-8 py-4 text-lg font-medium text-white transition hover:bg-primary-900 active:bg-primary-800 disabled:cursor-not-allowed disabled:bg-neutral-700 disabled:text-white disabled:hover:bg-neutral-700"
    >
      {nextLabel}
      <ArrowIcon />
    </button>
  </form>
);
